<template>
  <PageHeader title="Customers">
    <template #right>
      <Button
        v-if="$can('create_customer')"
        :label="$t('ui.crud.new', { item: $t('ui.entities.customer') })"
        icon="plus"
        @click="newCustomerModalIsOpen = true"
      />
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Customers"
      :value="visibleAgreements"
      :loading="agreementsAreLoading"
      removable-sort
      sort-field="created_at"
      :sort-order="-1"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :global-filter-fields="['id', 'forename', 'surname', 'dealershipName', 'make', 'model', 'vrm']"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-customers-agreements"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
    >
      <template #empty>
        <Empty
          :title="hasDealershipsSelected ? 'No agreements found' : 'Select a dealership'"
          :icon="hasDealershipsSelected ? 'file-contract' : 'building'"
        />
      </template>

      <template #header>
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="flex flex-wrap items-center gap-4">
            <Input
              v-model="filters.global.value"
              :placeholder="$t('ui.common.search', 1)"
              icon="search"
            />

            <Select
              v-model="selectedDealershipIds"
              :options="dealershipOptions"
              searchable
              mode="multiple"
              :chips="false"
              value-key="value"
              label-key="label"
              placeholder="Select dealerships"
              class="min-w-80 max-w-96"
            />

            <div class="toggle-container">
              <ToggleSwitch
                v-model="showEndedCustomers"
                size="sm"
              />
              <div class="font-medium">Show Ended Customers</div>
            </div>

            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </div>

          <Button
            label="CSV"
            type="button"
            severity="secondary"
            outlined
            icon="file-excel"
            @click="exportCSV()"
          />
        </div>
      </template>

      <Column
        field="surname"
        header="Customer"
        :sortable="true"
      >
        <template #body="{ data }">
          <router-link
            :to="'/agreement/' + data.id"
            class="link whitespace-nowrap"
          >
            {{ getCustomerName(data) }}
          </router-link>
        </template>
      </Column>

      <Column
        field="dealershipName"
        header="Dealership"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="whitespace-nowrap">{{ data.dealershipName }}</div>
        </template>
      </Column>

      <Column
        field="model"
        filter-field="make"
        header="Vehicle"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <div class="flex items-center gap-2 whitespace-nowrap">
            <span>{{ getVehicleName(data) }}</span>
            <!-- Only render a plate when the API returns a VRM. -->
            <LicensePlate
              v-if="data.vrm"
              :plate="data.vrm"
              country="uk"
              greyscale
              size="sm"
            />
          </div>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="makeOptions"
            searchable
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="min-w-56"
          />
        </template>
      </Column>

      <Column
        field="endDate"
        header="End Date"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <div class="whitespace-nowrap">{{ formatDate(data.endDate) }}</div>
        </template>
        <template #filter="{ filterModel }">
          <DatePicker
            v-model="filterModel.value"
            selection-mode="range"
            :close-on-select="false"
            placeholder="Any"
            class="min-w-60"
            :months-visible="2"
          />
        </template>
      </Column>

      <Column
        field="term"
        header="Term"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <div class="whitespace-nowrap">{{ data.term }}</div>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="termOptions"
            searchable
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="min-w-40"
          />
        </template>
      </Column>

      <Column
        field="created_at"
        header="Created"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="whitespace-nowrap">{{ formatDate(data.created_at) }}</div>
        </template>
      </Column>
    </DataTable>
  </Card>

  <teleport to="#modals-container">
    <Modal
      :is-open="newCustomerModalIsOpen"
      :buttons="[]"
      :has-unsaved-changes="newCustomerHasUnsavedChanges"
      overflow-behaviour="visible"
      @close-modal="((newCustomerModalIsOpen = false), (newCustomerHasUnsavedChanges = false))"
    >
      <NewCustomer @form-updated="newCustomerHasUnsavedChanges = true" />
    </Modal>
  </teleport>
</template>

<script setup lang="ts">
// composables
import useApiRequest from '@/composables/useApiRequest'

// components
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Button from '@/components/button/Button.vue'
import Modal from '@/components/modal/Modal.vue'
import Input from '@/components/forms/Input.vue'
import Select from '@/components/forms/Select.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import Empty from '@/components/empty/Empty.vue'
import LicensePlate from '@/components/license-plate/LicensePlate.vue'
import NewCustomer from '@/components/unique/forms/NewCustomer.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

// stores
import { useAuthStore } from '@/stores/AuthStore'

// types
import type { AgreementListApiItem, AgreementListItem } from '@/types/domain/agreement'

// utilities
import { FilterMatchMode, FilterService } from '@primevue/core/api'
import { computed, onMounted, ref, watch } from 'vue'
import { alertToast } from '@/utilities/notification'
import { getFormattedDate } from '@/utilities/dates'
import dayjs from 'dayjs'
import 'flatpickr/dist/flatpickr.css'

interface DealershipOption {
  value: number
  label: string
}

interface DataTableExportRef {
  exportCSV: () => void
}

/** Custom DataTable filter key for agreement end date ranges. */
const endDateRangeFilter = 'endDateRangeFilter'

/** Custom DataTable filter key for case-normalized vehicle makes. */
const vehicleMakeFilter = 'vehicleMakeFilter'

/** Local storage key for the selected dealership filter. */
const dealershipSelectionStorageKey = 'customers-agreements-dealerships-selected'

/** Auth store containing the dealerships this user is allowed to access. */
const authStore = useAuthStore()

/** Customers table reference for CSV export. */
const dt = ref<DataTableExportRef | null>(null)

/** Agreement rows displayed in the customer list. */
const agreements = ref<AgreementListItem[]>([])

/** Controls whether agreements with past end dates are shown. */
const showEndedCustomers = ref(false)

/** Tracks loading across one or more dealership requests. */
const agreementsAreLoading = ref(false)

/** API request helper for agreement list calls. */
const agreementsApiRequest = useApiRequest()

/** New customer modal visibility state. */
const newCustomerModalIsOpen = ref(false)

/** Tracks unsaved changes in the new customer modal. */
const newCustomerHasUnsavedChanges = ref(false)

/** Dealerships the current user can select in the filter. */
const dealershipOptions = computed<DealershipOption[]>(() => {
  return authStore.accessAllowed.dealerships.map(dealership => ({
    value: dealership.id,
    label: dealership.name
  }))
})

/** Selected dealership IDs used to fetch agreements. */
const selectedDealershipIds = ref<number[]>(getStoredDealershipSelection())

/** DataTable filter state. */
const filters = ref(getDefaultFilters())

/** True when at least one dealership has been selected. */
const hasDealershipsSelected = computed(() => selectedDealershipIds.value.length > 0)

/** Agreements filtered by the ended-customer toggle. */
const visibleAgreements = computed<AgreementListItem[]>(() => {
  // When enabled, include all customers returned by the API.
  if (showEndedCustomers.value) {
    return agreements.value
  }

  return agreements.value.filter(agreement => !agreementHasEnded(agreement))
})

/** Make options available in the vehicle column filter. */
const makeOptions = computed(() => {
  const makes = new Set<string>()

  agreements.value.forEach(agreement => {
    const make = normalizeVehicleMake(agreement.make)

    // Skip rows where the API did not return a make.
    if (make) {
      makes.add(make)
    }
  })

  return Array.from(makes)
    .sort((a, b) => a.localeCompare(b))
    .map(make => ({
      value: make,
      label: make
    }))
})

/** Term options available in the term column filter. */
const termOptions = computed(() => {
  const terms = new Set<number>()

  agreements.value.forEach(agreement => {
    if (typeof agreement.term === 'number') {
      terms.add(agreement.term)
    }
  })

  return Array.from(terms)
    .sort((a, b) => a - b)
    .map(term => ({
      value: term,
      label: String(term)
    }))
})

onMounted(() => {
  FilterService.register(endDateRangeFilter, isDateWithinRange)
  FilterService.register(vehicleMakeFilter, isVehicleMakeSelected)

  // Load immediately when a valid stored or default selection exists.
  if (hasDealershipsSelected.value) {
    void fetchAgreements()
  }
})

watch(selectedDealershipIds, () => {
  onDealershipChange()
})

watch(
  () => authStore.accessAllowed.dealerships.map(dealership => dealership.id),
  () => {
    setSingleDealershipDefault()
  }
)

/**
 * Gets the default dealership selection for the current user.
 * @returns A single dealership when it is the user's only allowed dealership; otherwise an empty selection.
 */
function getDefaultDealershipSelection(): number[] {
  // Auto-select only when there is no ambiguity for the user.
  if (authStore.accessAllowed.dealerships.length === 1) {
    return [authStore.accessAllowed.dealerships[0].id]
  }

  return []
}

/**
 * Selects the only accessible dealership when no dealership has been selected yet.
 */
function setSingleDealershipDefault(): void {
  // Preserve any user-selected dealerships.
  if (selectedDealershipIds.value.length > 0) {
    return
  }

  const defaultSelection = getDefaultDealershipSelection()

  // Only set a default when there is exactly one accessible dealership.
  if (defaultSelection.length === 0) {
    return
  }

  selectedDealershipIds.value = defaultSelection
}

/**
 * Reads and validates the persisted dealership selection.
 * @returns Allowed dealership IDs restored from local storage.
 */
function getStoredDealershipSelection(): number[] {
  try {
    const storedValue = localStorage.getItem(dealershipSelectionStorageKey)

    // Fall back to the default selection when nothing is stored.
    if (!storedValue) {
      return getDefaultDealershipSelection()
    }

    const parsedValue: unknown = JSON.parse(storedValue)

    // Ignore malformed stored selections.
    if (!Array.isArray(parsedValue)) {
      return getDefaultDealershipSelection()
    }

    const allowedDealershipIds = new Set(authStore.accessAllowed.dealerships.map(dealership => dealership.id))

    const selectedDealerships = parsedValue.filter((dealershipId): dealershipId is number => {
      return typeof dealershipId === 'number' && allowedDealershipIds.has(dealershipId)
    })

    // Use the default selection when stored IDs are no longer allowed.
    if (selectedDealerships.length === 0) {
      return getDefaultDealershipSelection()
    }

    return selectedDealerships
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Unable to read saved dealership filters'
    console.error(errorMessage)

    return getDefaultDealershipSelection()
  }
}

/**
 * Clears the global DataTable filters.
 */
function clearFilters(): void {
  sessionStorage.removeItem('dt-customers-agreements')
  showEndedCustomers.value = false
  filters.value = getDefaultFilters()
}

/**
 * Creates the default DataTable filter state.
 * @returns DataTable filters keyed by column field.
 */
function getDefaultFilters() {
  return {
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
    make: { value: null as string[] | null, matchMode: vehicleMakeFilter },
    endDate: { value: null as Array<Date | string | null> | null, matchMode: endDateRangeFilter },
    term: { value: null as number[] | null, matchMode: FilterMatchMode.IN }
  }
}

/**
 * Persists dealership changes and reloads the agreements list.
 */
function onDealershipChange(): void {
  localStorage.setItem(dealershipSelectionStorageKey, JSON.stringify(selectedDealershipIds.value))
  void fetchAgreements()
}

/**
 * Fetches agreements for all selected dealerships and merges the results.
 */
async function fetchAgreements(): Promise<void> {
  agreements.value = []

  // Avoid API calls until the user has selected a dealership.
  if (!hasDealershipsSelected.value) {
    return
  }

  agreementsAreLoading.value = true

  try {
    const agreementRows: AgreementListItem[] = []

    for (const dealershipId of selectedDealershipIds.value) {
      const response = await agreementsApiRequest.send<AgreementListApiItem[]>({
        endpoint: '/v1/agreements',
        params: {
          dealershipId
        }
      })

      // Append only successful agreement list responses.
      if (response.success && Array.isArray(response.data)) {
        agreementRows.push(
          ...response.data.map(agreement => ({
            ...agreement,
            dealershipId
          }))
        )
      } else {
        alertToast('Failed to fetch agreements', response.message ?? 'Unknown error', 'error')
      }
    }

    agreements.value = agreementRows
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Unable to fetch agreements'
    alertToast('Failed to fetch agreements', errorMessage, 'error')
  } finally {
    agreementsAreLoading.value = false
  }
}

/**
 * Formats a nullable date for table display.
 * @param date - Date string returned by the API.
 * @returns A formatted date or an empty string.
 */
function formatDate(date: string | null): string {
  // Empty dates should render as blank cells.
  if (!date) {
    return ''
  }

  return getFormattedDate(date, 'D MMM YYYY', { local: true })
}

/**
 * Builds the display name for an agreement customer.
 * @param agreement - Agreement list row.
 * @returns Customer name for the table.
 */
function getCustomerName(agreement: AgreementListItem): string {
  const customerName = [agreement.forename, agreement.surname].filter(Boolean).join(' ')

  // Fall back when the API has no customer name fields.
  if (!customerName) {
    return 'Unknown Customer'
  }

  return customerName
}

/**
 * Builds the display label for an agreement vehicle.
 * @param agreement - Agreement list row.
 * @returns Vehicle make and model for the table.
 */
function getVehicleName(agreement: AgreementListItem): string {
  const vehicleName = [agreement.make, agreement.model].filter(Boolean).join(' ')

  // Fall back when the API has no vehicle fields.
  if (!vehicleName) {
    return 'Unknown Vehicle'
  }

  return vehicleName
}

/**
 * Checks whether an agreement has ended.
 * @param agreement - Agreement list row.
 * @returns True when the agreement end date is before today.
 */
function agreementHasEnded(agreement: AgreementListItem): boolean {
  // Rows without a valid end date should stay visible.
  if (!agreement.endDate || !dayjs(agreement.endDate).isValid()) {
    return false
  }

  return dayjs(agreement.endDate).isBefore(dayjs(), 'day')
}

/**
 * Checks whether a vehicle make matches the selected uppercase make filters.
 * @param value - Vehicle make from the table row.
 * @param filter - Selected uppercase make filters.
 * @returns True when the row should be included.
 */
function isVehicleMakeSelected(value: unknown, filter: unknown): boolean {
  const selectedMakes = Array.isArray(filter) ? filter : []

  // An empty make selection should not filter rows.
  if (selectedMakes.length === 0) {
    return true
  }

  const make = normalizeVehicleMake(value)

  // Rows without a make should not match an active make filter.
  if (!make) {
    return false
  }

  return selectedMakes.includes(make)
}

/**
 * Normalizes vehicle make values for filter display and comparison.
 * @param value - Raw vehicle make value.
 * @returns Uppercase make value or null.
 */
function normalizeVehicleMake(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }

  const make = value.trim()

  // Blank make values should not appear in the filter list.
  if (!make) {
    return null
  }

  return make.toUpperCase()
}

/**
 * Checks if a DataTable date value falls inside the selected date range.
 * @param value - Date value from the table row.
 * @param filter - DatePicker range value from the column filter.
 * @returns True when the row should be included.
 */
function isDateWithinRange(value: unknown, filter: unknown): boolean {
  const selectedRange = Array.isArray(filter) ? filter : []
  const start = parseFilterDate(selectedRange[0])
  const end = parseFilterDate(selectedRange[1])
  const rowDate = parseFilterDate(value)

  // An empty range should not filter rows.
  if (!start && !end) {
    return true
  }

  // Invalid row dates are hidden only when a date filter is active.
  if (!rowDate) {
    return false
  }

  if (start && rowDate.isBefore(start, 'day')) {
    return false
  }

  if (end && rowDate.isAfter(end, 'day')) {
    return false
  }

  return true
}

/**
 * Parses DataTable and DatePicker values into dayjs instances.
 * @param value - Raw date-like value.
 * @returns A valid dayjs date or null.
 */
function parseFilterDate(value: unknown): dayjs.Dayjs | null {
  if (!value) {
    return null
  }

  if (!(typeof value === 'string' || typeof value === 'number' || value instanceof Date || dayjs.isDayjs(value))) {
    return null
  }

  const parsedDate = dayjs(value)

  if (!parsedDate.isValid()) {
    return null
  }

  return parsedDate
}

/**
 * Exports the visible table rows as CSV.
 */
function exportCSV(): void {
  dt.value?.exportCSV()
}
</script>

<style>
.toggle-container {
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-md);
  padding: var(--s-2) var(--s-3) var(--s-2) var(--s-2);
  text-align: center;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-secondary);
}
</style>
